import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import FrameworkUtils from 'utils/framework';
import { userSelector } from 'selectors/user/userSelector';
import basketSelector from 'selectors/basket/basketSelector';
import BeautyInsiderSelector from 'selectors/beautyInsider/beautyInsiderSelector';
import biUtils from 'utils/BiProfile';
import userUtils from 'utils/User';
import localeUtils from 'utils/LanguageLocale';
import BeautyInsiderBirthdayGiftsEN from 'components/Content/BeautyInsider/BeautyInsiderBirthdayGifts/locales/BeautyInsiderBirthdayGifts_en_US';
import BeautyInsiderBirthdayGiftsFR from 'components/Content/BeautyInsider/BeautyInsiderBirthdayGifts/locales/BeautyInsiderBirthdayGifts_fr_CA';
import Empty from 'constants/empty';

const { wrapHOC } = FrameworkUtils;
const { beautyInsiderSelector } = BeautyInsiderSelector;
const { REWARD_GROUPS } = biUtils;

const getText = localeUtils.getLocalisedDataFromResource(BeautyInsiderBirthdayGiftsEN, BeautyInsiderBirthdayGiftsFR);

/**
 * Resolves the days-to-redeem message to display.
 * For English, returns the raw date string from the API as-is.
 * For French, extracts the numeric day count and formats it using the locale resource.
 *
 * @param {string|null|undefined} bdGiftLastDateToRedeem - The raw last-date-to-redeem string from the user account (e.g. "5 days left")
 * @returns {string|null} Localized days-to-redeem message, or null if not available
 */
const getDaysToRedeemMsg = bdGiftLastDateToRedeem => {
    if (!bdGiftLastDateToRedeem) {
        return null;
    }

    if (!localeUtils.isFrench()) {
        return bdGiftLastDateToRedeem;
    }

    const daysLeft = bdGiftLastDateToRedeem.match(/\d+/);

    return daysLeft ? getText('daysToRedeem', [daysLeft[0]]) : null;
};

/**
 * Reselect selector that derives props for BeautyInsiderBirthdayGifts from the Redux store.
 * Combines user, basket, and beautyInsider slices to produce birthday gift data and localized strings.
 *
 * @returns {{
 *   birthdayGifts: object[]|undefined,
 *   firstName: string,
 *   isAnonymous: boolean,
 *   basket: object,
 *   localization: {
 *     happyBday: string,
 *     chooseGift: string,
 *     daysToRedeemMsg: string|null,
 *     viewAll: string
 *   }
 * }}
 */
const fields = createSelector(userSelector, basketSelector, beautyInsiderSelector, (user, basket, beautyInsider) => {
    const birthdayGifts = (beautyInsider.biRewardGroups || Empty.Object)[REWARD_GROUPS.BIRTHDAY];
    const daysToRedeemMsg = getDaysToRedeemMsg(user.beautyInsiderAccount?.bdGiftLastDateToRedeem);

    return {
        birthdayGifts,
        firstName: user.firstName,
        isAnonymous: user.isInitialized && userUtils.isAnonymous(),
        basket,
        localization: {
            happyBday: getText('happyBday'),
            chooseGift: getText('chooseGift'),
            daysToRedeemMsg,
            viewAll: getText('viewAll')
        }
    };
});

const functions = () => ({});

const withBeautyInsiderBirthdayGiftsProps = wrapHOC(connect(fields, functions));

export {
    withBeautyInsiderBirthdayGiftsProps, fields, functions
};
